import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Card from '../components/Card';
import { KeyIcon, ShieldIcon, CheckIcon, InfoIcon, XIcon, EyeIcon, EyeOffIcon } from '../components/Icons';
import { isDevEnvironment } from '../config/env';

interface LoginProps {
    onLoginSuccess?: () => void;
}

const REMEMBER_DOCUMENTO_KEY = 'location-proximity-remember-documento';
const SAVED_DOCUMENTO_KEY = 'location-proximity-saved-documento';
const SAVED_PASSWORD_KEY = 'location-proximity-saved-password';

export default function Login({ onLoginSuccess }: LoginProps) {
    const { login: authLogin } = useAuth();
    const [documento, setDocumento] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rememberMe, setRememberMe] = useState(false);
    const [loginSuccess, setLoginSuccess] = useState(false);

    useEffect(() => {
        const shouldRemember = localStorage.getItem(REMEMBER_DOCUMENTO_KEY) === 'true';
        if (shouldRemember) {
            const savedDocumento = localStorage.getItem(SAVED_DOCUMENTO_KEY) || '';
            const savedPassword = localStorage.getItem(SAVED_PASSWORD_KEY) || '';

            setDocumento(savedDocumento);
            setPassword(savedPassword);
            setRememberMe(true);
        }
    }, []);

    const handleRememberChange = (checked: boolean) => {
        setRememberMe(checked);

        if (!checked) {
            localStorage.removeItem(REMEMBER_DOCUMENTO_KEY);
            localStorage.removeItem(SAVED_DOCUMENTO_KEY);
            localStorage.removeItem(SAVED_PASSWORD_KEY);
        }
    };

    function formatCPF(value: string) {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
            .slice(0, 14);
    }

    const handleDocumentoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '');
        setDocumento(formatCPF(raw));
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!documento.trim() || !password.trim()) {
            setError('Por favor, preencha todos os campos');
            return;
        }

        if (password.length < 3) {
            setError('A senha deve ter pelo menos 3 caracteres');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Normalizar documento para uppercase antes de enviar
            const normalizedDocumento = documento.trim().toUpperCase();
            const result = await authLogin(normalizedDocumento, password);

            if (result.success && result.user) {
                setLoginSuccess(true);

                // Gerenciar dados salvos baseado na opção "lembrar"
                if (rememberMe) {
                    // Salvar credenciais no localStorage
                    localStorage.setItem(REMEMBER_DOCUMENTO_KEY, 'true');
                    localStorage.setItem(SAVED_DOCUMENTO_KEY, normalizedDocumento);
                    localStorage.setItem(SAVED_PASSWORD_KEY, password);
                } else {
                    // Limpar dados salvos se "lembrar" não estiver marcado
                    localStorage.removeItem(REMEMBER_DOCUMENTO_KEY);
                    localStorage.removeItem(SAVED_DOCUMENTO_KEY);
                    localStorage.removeItem(SAVED_PASSWORD_KEY);
                }

                // Redireciona imediatamente e também com delay visual
                onLoginSuccess?.();

                // Delay adicional apenas para feedback visual (não bloqueia redirecionamento)
                setTimeout(() => {
                    // Garantia adicional caso o primeiro callback tenha falhado
                    onLoginSuccess?.();
                }, 800);

            } else {
                setError(result.error || 'Documento ou senha incorretos');
            }
        } catch (err: any) {
            setError('Erro ao fazer login. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md space-y-6">
                {/* Card principal de Login */}
                <Card
                    variant="elevated"
                    padding="xl"
                    rounded="lg"
                    className="backdrop-blur-sm bg-white/90 border-0 shadow-2xl"
                >
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                            <ShieldIcon size={32} className="text-blue-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900">Bem-vindo</h1>
                        <p className="text-gray-600 mt-2">Entre em sua conta para continuar</p>
                        
                        {/* Credenciais de desenvolvimento */}
                        {isDevEnvironment() && (
                            <Card
                                variant="filled"
                                padding="sm"
                                rounded="md"
                                className="bg-yellow-50 border-yellow-200 mt-4"
                            >
                                <div className="text-yellow-800 text-xs">
                                    <p className="font-semibold mb-1">🔧 Modo Desenvolvimento</p>
                                    <p>admin/123456 • test/test123 • user/user123</p>
                                </div>
                            </Card>
                        )}
                    </div>

                    {/* Mensagem de erro */}
                    {error && (
                        <Card
                            variant="filled"
                            padding="sm"
                            rounded="md"
                            className="bg-red-50 border-red-200 mb-4"
                        >
                            <div className="flex items-center">
                                <XIcon size={20} className="text-red-500 mr-3 flex-shrink-0" />
                                <span className="text-red-700 text-sm">{error}</span>
                            </div>
                        </Card>
                    )}

                    {/* Mensagem de sucesso */}
                    {loginSuccess && (
                        <Card
                            variant="filled"
                            padding="sm"
                            rounded="md"
                            className="bg-green-50 border-green-200 mb-4"
                        >
                            <div className="flex items-center">
                                <CheckIcon size={20} className="text-green-500 mr-3 flex-shrink-0" />
                                <span className="text-green-700 text-sm">Login realizado com sucesso!</span>
                            </div>
                        </Card>
                    )}

                    {/* Formulário */}
                    <form onSubmit={handleLogin} className="space-y-6">
                        {/* Campo Documento */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Documento
                            </label>
                            <Card
                                variant="outlined"
                                padding="sm"
                                rounded="md"
                                className="focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
                            >
                                <div className="flex items-center">
                                    <div className="flex justify-between items-center w-[90%]">
                                        <InfoIcon size={20} className="w-[10%] text-gray-400 mr-3" />
                                        <input
                                            type="text"
                                            placeholder="Seu CPF"
                                            className="w-[90%] py-2 border-none outline-none bg-transparent"
                                            value={documento}
                                            onChange={handleDocumentoChange}
                                            maxLength={14}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>

                        {/* Campo Senha */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Senha
                            </label>
                            <Card
                                variant="outlined"
                                padding="sm"
                                rounded="md"
                                className="focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
                            >
                                <div className="flex items-center">
                                    <div className="flex justify-between items-center w-[90%]">
                                        <KeyIcon size={20} className="w-[10%] text-gray-400 mr-3" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Sua senha"
                                            className="w-[90%] py-2 border-none outline-none bg-transparent"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            disabled={loading}
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="w-[10%] text-gray-400 hover:text-gray-600 transition-colors"
                                        disabled={loading}
                                    >
                                        {showPassword ? (
                                            <EyeOffIcon size={16} />
                                        ) : (
                                            <EyeIcon size={16} />
                                        )}
                                    </button>
                                </div>
                            </Card>
                        </div>

                        {/* Checkbox lembrar-me */}
                        <div className="flex items-center justify-between">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={e => handleRememberChange(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                    disabled={loading}
                                />
                                <span className="ml-2 text-sm text-gray-600">Lembrar login e senha</span>
                            </label>
                        </div>

                        {/* Botão de Login */}
                        <button
                            type="submit"
                            className={`w-full p-3 text-white rounded-lg font-medium uppercase transition-colors duration-200 ${loading || !documento.trim() || !password.trim()
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-500 hover:bg-blue-600'
                                }`}
                            disabled={loading || !documento.trim() || !password.trim()}
                        >
                            {loading ? 'Entrando...' : 'Entrar'}
                        </button>
                    </form>
                </Card>

                {/* Footer */}
                <div className="text-center text-sm text-gray-500">
                    <p>© 2025 Sistema de Proximidade. Todos os direitos reservados.</p>
                </div>
            </div>
        </div>
    );
}
